// useFrequentRequests — client-side "frequently requested" tracker, ported from
// the browser extension (src/hooks/useFrequentRequests.ts). There is no backend
// signal for this; it's a local heuristic: every time the member submits an
// access request we bump a counter for that resource, then the Home "Frequent
// Access" card surfaces the top few for one-tap renewal.
//
// Differences from the extension: the portal is a web app, so we use
// localStorage (the extension used chrome.storage.local), and we scope the key
// by member id so a shared browser never leaks one member's history to another.

import { useCallback, useEffect, useState } from "react";

import { useMemberAuthStore } from "modules/members/auth";

const KEY_PREFIX = "ploy_frequent_requests";
const MAX_ENTRIES = 10;

export interface FrequentRequest {
  selectableResourceId: string;
  catalogItemId: string;
  catalogId: string;
  catalogItemName: string;
  appName: string;
  appIcon?: string;
  /** Display name, tenant-suffixed when the app's domain is shared. */
  resourceName: string;
  /** The link's own name; absent on entries saved before tenant suffixes. */
  rawResourceName?: string;
  lastRequestedAt: number; // timestamp (ms)
  requestCount: number;
  lastDuration?: { value: number; unit: string };
  lastIndefinite?: boolean;
  lastComments?: string;
}

export type FrequentRequestTrackInput = Omit<
  FrequentRequest,
  "lastRequestedAt" | "requestCount"
>;

const storageKey = (memberId: string | null | undefined): string =>
  `${KEY_PREFIX}:${memberId || "anon"}`;

const read = (key: string): FrequentRequest[] => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? (parsed as FrequentRequest[]) : [];
  } catch {
    return [];
  }
};

export const useFrequentRequests = () => {
  const memberId = useMemberAuthStore((s) => s.id);
  const key = storageKey(memberId);

  // Lazy init reads localStorage synchronously so the card doesn't flash its
  // empty state before the entries load.
  const [entries, setEntries] = useState<FrequentRequest[]>(() => read(key));

  // Re-read if the scoping key changes (e.g. a different member signs in).
  useEffect(() => {
    setEntries(read(key));
  }, [key]);

  const trackRequest = useCallback(
    (request: FrequentRequestTrackInput) => {
      setEntries((prev) => {
        const existing = prev.find(
          (e) => e.selectableResourceId === request.selectableResourceId,
        );

        let updated: FrequentRequest[] = existing
          ? prev.map((e) =>
              e.selectableResourceId === request.selectableResourceId
                ? {
                    ...e,
                    ...request,
                    lastRequestedAt: Date.now(),
                    requestCount: e.requestCount + 1,
                  }
                : e,
            )
          : [
              ...prev,
              { ...request, lastRequestedAt: Date.now(), requestCount: 1 },
            ];

        // Cap entries — evict least-recently-used.
        if (updated.length > MAX_ENTRIES) {
          updated = [...updated]
            .sort((a, b) => b.lastRequestedAt - a.lastRequestedAt)
            .slice(0, MAX_ENTRIES);
        }

        try {
          localStorage.setItem(key, JSON.stringify(updated));
        } catch {
          /* quota / disabled storage — keep in-memory only */
        }
        return updated;
      });
    },
    [key],
  );

  // Top entries by requestCount desc, then recency. Optionally drops items the
  // member already has access to / already has a pending request for.
  const getTopRequests = useCallback(
    (
      limit = 5,
      activeAccessIds?: Set<string>,
      activeRequestIds?: Set<string>,
    ): FrequentRequest[] => {
      let filtered = entries;
      if (activeAccessIds)
        filtered = filtered.filter(
          (e) => !activeAccessIds.has(e.selectableResourceId),
        );
      if (activeRequestIds)
        filtered = filtered.filter(
          (e) => !activeRequestIds.has(e.selectableResourceId),
        );

      return [...filtered]
        .sort((a, b) =>
          b.requestCount !== a.requestCount
            ? b.requestCount - a.requestCount
            : b.lastRequestedAt - a.lastRequestedAt,
        )
        .slice(0, limit);
    },
    [entries],
  );

  return { entries, trackRequest, getTopRequests };
};
